<template>
  <div class="memory-board text--text">
    <section class="b-container">
      <h1 class="text-h5 mb-4">
        Доска памяти врачей
      </h1>
      <p class="text-body-2 mb-6">
        Светлая память тем, кто посвятил свою жизнь помощи другим.
      </p>

      <div class="mb-4">
        <CoreSearchField
          :value="searchQuery"
          maxlength="150"
          autocomplete="off"
          placeholder="ФИО врача"
          class="mb-2"
          :debounce-ms="500"
          @input:debounced="searchQuery = $event"
        />
        <DoctorFilter
          v-model="selectedCity"
        />
      </div>

      <DoctorListEmpty
        v-if="searchQuery && !doctors.length"
        :search-query="searchQuery"
        class="py-2"
      />
      <template
        v-else
      >
        <div class="memory-board__cards">
          <DoctorCard
            v-for="(doctor, i) in doctors"
            :key="i"
            :doctor="doctor"
          />
        </div>
        <div
          v-if="isExistNextPage"
          class="mt-8 w-md-33 mx-auto"
        >
          <v-btn
            v-if="error"
            block
            depressed
            variant="text"
            color="primary"
            @click="error = false"
          >
            Загрузить ещё
          </v-btn>
          <v-progress-circular
            v-else
            v-intersect="onIntersect"
            color="primary"
            class="d-flex mx-auto"
            :width="2.5"
            :size="32"
            indeterminate
          />
        </div>
      </template>

      <v-fade-transition>
        <div
          v-show="isVisibleBtnScroll"
          class="memory-board__scroll-button"
        >
          <CoreScrollUpButton class="b-container d-flex justify-end" />
        </div>
      </v-fade-transition>

      <CoreSnackbar />
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  onBeforeUnmount,
  onMounted,
  onServerPrefetch,
  ref,
  watch,
} from 'vue'
import useSsrState from 'src/composables/useSsrState'
import apiDoctors from 'src/components/pages/memorial/MemoryBoardPage/api/apiDoctors'
import CoreSnackbar from 'src/core/CoreSnackbar/CoreSnackbar.vue'
import DoctorCard from 'src/components/pages/memorial/MemoryBoardPage/components/DoctorCard.vue'
import CoreSearchField from 'src/core/CoreSearchField/CoreSearchField.vue'
import DoctorFilter from 'src/components/pages/memorial/MemoryBoardPage/components/DoctorFilter/DoctorFilter.vue'
import DoctorListEmpty from 'src/components/pages/memorial/MemoryBoardPage/components/DoctorListEmpty.vue'
import CoreScrollUpButton from 'src/core/CoreScrollUpButton/CoreScrollUpButton.vue'
import type { ApiDoctorsResponseGet } from 'src/components/pages/memorial/MemoryBoardPage/api/apiDoctors.type'
import type { ApiTownResponseGet } from 'src/components/pages/memorial/MemoryBoardPage/api/apiTownList.type'

type MemorialBoardState = {
  doctors: ApiDoctorsResponseGet['doctors']
  page: number
  has_next: boolean
}

const memorialBoard = useSsrState<MemorialBoardState>()

const doctors = ref<ApiDoctorsResponseGet['doctors']>(memorialBoard.value?.doctors ?? [])
const error = ref(false)
const page = ref(memorialBoard.value?.page ?? 1)
const searchQuery = ref('')
const selectedCity = ref<ApiTownResponseGet[number]>()
const isExistNextPage = ref(memorialBoard.value?.has_next ?? true)
const isVisibleBtnScroll = ref(false)
const scrollThreshold = 500 // Пиксели, после которых кнопка появляется

const loadDoctors = async () => {
  error.value = false

  try {
    const { data } = await apiDoctors.get({
      page: page.value,
      query: searchQuery.value || undefined,
      town_id: selectedCity.value?.id,
    })

    doctors.value = page.value === 1 ? data.doctors : [...doctors.value, ...data.doctors]
    isExistNextPage.value = data.has_next

    if (data.has_next) {
      page.value += 1
    }

    memorialBoard.value = {
      doctors: doctors.value,
      page: page.value,
      has_next: isExistNextPage.value,
    }
  } catch {
    error.value = true
  }
}

onServerPrefetch(async () => {
  if (!doctors.value.length) {
    await loadDoctors()
  }
})

const onIntersect = (_entries: unknown, _observer: unknown, isIntersecting: boolean) => {
  if (isIntersecting) {
    loadDoctors()
  }
}

watch([searchQuery, selectedCity], () => {
  page.value = 1
  loadDoctors()
})

const handleScroll = () => {
  isVisibleBtnScroll.value = window.scrollY > scrollThreshold
}

onMounted(() => {
  if (!doctors.value.length) {
    loadDoctors()
  }

  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/mixins';

.memory-board {
  &__cards {
    display: grid;
    flex-wrap: wrap;
    grid-template-columns: 1fr;
    gap: 16px;

    @include media-breakpoint-up(md) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  &__scroll-button {
    position: fixed;
    bottom: 40px;
    left: 0;
    width: 100%;
    z-index: 20;
    pointer-events: none;

    :deep(button) {
      pointer-events: auto;
    }
  }
}
</style>
